/** Loading / error / empty blocks every page shows, and the one error toaster. */

import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useUiStore } from "@/stores/ui-store";

export function Loading({ what }: { what: string }) {
  return (
    <div role="status" className="flex items-center gap-2 px-5 py-12 text-sm text-zinc-500">
      <Loader2 className="animate-spin" size={16} />
      Loading {what}…
    </div>
  );
}

export function LoadError({ what, error }: { what: string; error: Error }) {
  return (
    <p className="rounded-xl bg-red-50 px-5 py-4 text-sm text-red-800 ring-1 ring-red-200 dark:bg-red-500/10 dark:text-red-200 dark:ring-red-500/20">
      Couldn't load {what}: {error.message}
    </p>
  );
}

/** A zero state says what goes here and offers the one action that fills it. */
export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{title}</p>
      {children && <p className="max-w-sm text-sm text-zinc-500 dark:text-zinc-400">{children}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Toast a failed mutation. The server's own words when they explain the refusal. */
export function useReportError() {
  const notify = useUiStore((s) => s.notify);
  return (err: unknown) =>
    notify(
      err instanceof ApiError && (err.isUserFixable || err.status === 403)
        ? err.detail
        : "Couldn't save that — the server did not answer. Try again in a moment.",
      "error",
    );
}
